import { PeriodValue, RangeKey, fitPeriod, resolvePeriod } from '../../../components/design-system/analytics';
import { useHistoryWindow } from '../../../components/design-system/plan/HistoryLimitNotice';
import { useMemo, useState } from 'react';

import type { InsightsRange } from '../../../common/hooks/use-shopper-insights-api.hook';

/**
 * The date range the Shopping Assistant pages share, as the Leads page does it:
 * a preset key ('7d', '30d', …) or a custom pair from the picker.
 */
/**
 * The period the assistant pages read, on the same control as the other
 * analytics pages — one way to pick "last 30 days" across the backoffice.
 */
export const useInsightsRange = (initial: RangeKey = '30d') => {
    const [chosen, setValue] = useState<PeriodValue>({ key: initial });
    // The plan's history window (Free: 30 days) — a longer range reads as the longest that fits.
    const { maxDays } = useHistoryWindow();
    const value = useMemo(() => fitPeriod(chosen, maxDays) as PeriodValue, [chosen, maxDays]);
    const range: InsightsRange = useMemo(() => {
        const period = resolvePeriod(value.key, { start: value.start, end: value.end });
        return { startDate: period.startDate, endDate: period.endDate };
    }, [value]);
    return { range, periodProps: { value, onChange: setValue, maxDays } };
};

export const formatPercent = (value: number | null | undefined) =>
    value === null || value === undefined ? '—' : `${Math.round(value * 100)}%`;

export const formatCost = (usd: number) => `$${usd < 1 ? usd.toFixed(3) : usd.toFixed(2)}`;

export const formatSeconds = (ms: number | null | undefined) =>
    ms === null || ms === undefined ? '—' : `${(ms / 1000).toFixed(1)}s`;

/**
 * A ranked list with a thin proportion bar under each label.
 *
 * Not a chart library: these are a handful of categories whose job is ranking,
 * and a labelled list with the count in text reads faster than bars with a
 * legend. The bar is ink at low opacity — identity is the label, never colour.
 */
export const BarList = ({
    items,
    empty,
}: {
    items: Array<{ label: string; value: number; hint?: string }>;
    empty: string;
}) => {
    const max = Math.max(1, ...items.map((i) => i.value));
    if (!items.length) return <p className="py-6 text-center text-sm text-muted-foreground">{empty}</p>;
    return (
        <ul className="flex flex-col gap-3">
            {items.map((item) => (
                <li key={item.label} className="flex flex-col gap-1.5">
                    <div className="flex items-baseline justify-between gap-3 text-sm">
                        <span className="min-w-0 truncate" title={item.label}>{item.label}</span>
                        <span className="shrink-0 tabular-nums text-muted-foreground">
                            {item.value}
                            {item.hint ? <span className="ms-1 text-xs">{item.hint}</span> : null}
                        </span>
                    </div>
                    <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-foreground/60" style={{ width: `${(item.value / max) * 100}%` }} />
                    </div>
                </li>
            ))}
        </ul>
    );
};
